import React, {
  FC, useState, ChangeEvent, useMemo, MouseEvent, useEffect,
} from 'react';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import AddIcon from '@material-ui/icons/Add';
import { IconButton } from '@/ui/IconButton';
import { v4 as uuidv4 } from 'uuid';
import useStyles from './styles';
import { IProps } from './types';
import {
  DataEventsEnum, DataReportsEnum, IFields, TypeStatisticsEnum, DataAdvertisersEnum,
  DataPublishersEnum, DataJobsEnum, DataCampaignsEnum, DataTqmEnum, DataImportEnum, DataExportEnum, TypeStatistics,
} from '../types';
import {
  listDataEvents, listDataReports, listTypeStatistics, listDataAdvertisers, listDataJobs,
  listDataPublishers, listDataCampaigns, listDataTqm, listDataImport, listDataExport,
} from '../config';

const AddCardStatistics: FC<IProps> = ({ handleAdd, type, color }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const initFields = useMemo(() => (type
    ? { type, field: getField(type) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    : { type: TypeStatisticsEnum.EVENTS, field: DataEventsEnum.COUNT }), [type]);

  const [fields, setFields] = useState<IFields>({});

  useEffect(() => {
    setFields(initFields);
  }, [initFields]);

  const listData = useMemo<{ key: string; value: string}[]>(() => {
    switch (fields.type) {
      case TypeStatisticsEnum.EVENTS:
        return listDataEvents();
      case TypeStatisticsEnum.REPORTS:
        return listDataReports();
      case TypeStatisticsEnum.ADVERTISERS:
        return listDataAdvertisers;
      case TypeStatisticsEnum.PUBLISHERS:
        return listDataPublishers;
      case TypeStatisticsEnum.JOBS:
        return listDataJobs();
      case TypeStatisticsEnum.CAMPAIGNS:
        return listDataCampaigns;
      case TypeStatisticsEnum.TQM:
        return listDataTqm;
      case TypeStatisticsEnum.IMPORT:
        return listDataImport;
      case TypeStatisticsEnum.EXPORT:
        return listDataExport;
      default:
        return [];
    }
  }, [fields.type]);

  return (
    <>
      {(() => {
        switch (color) {
          case 'secondary': return (
            <Card className={classes.cardSecondary}>
              <CardContent>
                <IconButton aria-label="add" size="small" onClick={handleOpen} className={classes.containerButton}>
                  <AddIcon className={classes.icon} />
                </IconButton>
              </CardContent>
            </Card>
          );
          default: return (
            <Card className={classes.card}>
              <CardContent>
                <IconButton aria-label="add" size="small" onClick={handleOpen} className={classes.containerButton}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          );
        }
      })()}
      <Popover
        id="preset-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div className={classes.popover}>
          {
            !type && (
              <FormControl variant="filled">
                <TextField
                  autoComplete="off"
                  size="small"
                  select
                  variant="filled"
                  label="Type:"
                  className={classes.select}
                  onChange={handleChange}
                  value={fields.type}
                >
                  {
                    listTypeStatistics().map((item) => (
                      <MenuItem key={`${item.key}-type`} value={item.key}>{item.value}</MenuItem>
                    ))
                  }
                </TextField>
              </FormControl>
            )
          }
          <FormControl variant="filled">
            <TextField
              autoComplete="off"
              size="small"
              select
              variant="filled"
              label="Data:"
              className={classes.select}
              onChange={(
                { target: { value } }: ChangeEvent<{ value: string }>,
              ) => setFields({ ...fields, field: value as TypeStatistics })}
              value={fields.field}
            >
              {
                listData.map((item) => (
                  <MenuItem key={`${item.key}-filed`} value={item.key}>{item.value}</MenuItem>
                ))
              }
            </TextField>
          </FormControl>
          <div className={classes.saveWrap}>
            <Button color="secondary" onClick={handle}>Add</Button>
          </div>
        </div>
      </Popover>
    </>
  );

  function handleOpen(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function getList(type: string) {
    switch (type) {
      case TypeStatisticsEnum.EVENTS:
        return listDataEvents();
      case TypeStatisticsEnum.REPORTS:
        return listDataReports();
      case TypeStatisticsEnum.ADVERTISERS:
        return listDataAdvertisers;
      case TypeStatisticsEnum.PUBLISHERS:
        return listDataPublishers;
      case TypeStatisticsEnum.JOBS:
        return listDataJobs();
      case TypeStatisticsEnum.CAMPAIGNS:
        return listDataCampaigns;
      case TypeStatisticsEnum.TQM:
        return listDataTqm;
      case TypeStatisticsEnum.IMPORT:
        return listDataImport;
      case TypeStatisticsEnum.EXPORT:
        return listDataExport;
      default:
        return [];
    }
  }

  function getField(type: string) {
    const list = getList(type);
    switch (type) {
      case TypeStatisticsEnum.EVENTS:
        return DataEventsEnum.COUNT;
      case TypeStatisticsEnum.REPORTS:
        return list.length ? (list[0].key as DataReportsEnum) : DataReportsEnum.CLICKS;
      case TypeStatisticsEnum.ADVERTISERS:
        return DataAdvertisersEnum.REVENUE;
      case TypeStatisticsEnum.PUBLISHERS:
        return DataPublishersEnum.COST;
      case TypeStatisticsEnum.JOBS:
        return DataJobsEnum.CPA;
      case TypeStatisticsEnum.CAMPAIGNS:
        return DataCampaignsEnum.COUNT_JOBS;
      case TypeStatisticsEnum.TQM:
        return DataTqmEnum.TQF_CR;
      case TypeStatisticsEnum.IMPORT:
        return DataImportEnum.YREVENUE;
      case TypeStatisticsEnum.EXPORT:
        return DataExportEnum.MAX_CPC;
      default:
        return undefined;
    }
  }

  function handleChange({ target: { value } }: ChangeEvent<{ value: string }>) {
    const type = value as TypeStatisticsEnum;
    const field = getField(type);
    setFields({ ...fields, type, field });
  }

  function handle() {
    handleAdd({ ...fields, id: uuidv4() });
    handleClose();
  }

  function handleClose() {
    setAnchorEl(null);
  }
};

export default AddCardStatistics;
